import { useState, useEffect, useRef } from 'react'
import { X, Download, Share, Smartphone } from 'lucide-react'
import { useT } from '../i18n/useT'
import { isTourDone } from './OnboardingTour'
import { isMobile, isStandalone, SHOW_INSTALL_EVENT } from '../lib/pwa'
import { supabase } from '../lib/supabase'
import useAuthStore from '../store/authStore'

const DISMISS_KEY  = 'mindesk-install-dismissed'
const DISMISS_DAYS = 7

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export default function InstallPrompt() {
  const t = useT()
  const user           = useAuthStore(s => s.user)
  const refreshProfile = useAuthStore(s => s.refreshProfile)

  const [show, setShow]         = useState(false)
  const [platform, setPlatform] = useState('android') // 'android' | 'ios'
  const [forced, setForced]     = useState(false) // on-demand show (bypasses dismissed)
  const deferredPrompt          = useRef(null)

  const tryShow = useRef((force = false) => {
    if (isStandalone()) return false
    if (!isMobile()) return false

    if (!force) {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0)
      const dismissed   = dismissedAt && (Date.now() - dismissedAt < DISMISS_DAYS * 86400000)
      if (dismissed) return false
    }

    if (isIOS()) {
      setPlatform('ios')
      setForced(force)
      setShow(true)
      return true
    }
    // Android: mostra sempre. Se Chrome non ha ancora emesso
    // beforeinstallprompt (engagement non raggiunto), facciamo cadere il
    // banner sulle istruzioni manuali — meglio che non mostrare nulla.
    setPlatform('android')
    setForced(force)
    setShow(true)
    return true
  })

  useEffect(() => {
    // Capture beforeinstallprompt early (even during tour)
    const capturePrompt = (e) => {
      e.preventDefault()
      deferredPrompt.current = e
      // Show immediately if tour is already done (mobile only)
      if (isTourDone() && isMobile()) {
        tryShow.current()
      }
    }
    window.addEventListener('beforeinstallprompt', capturePrompt)

    // If tour is already done (returning user), try to show now
    if (isTourDone()) {
      setTimeout(() => tryShow.current(), 2000)
    }

    // Listen for tour completion to show the prompt
    const onTourComplete = () => setTimeout(() => tryShow.current(), 500)
    window.addEventListener('tour-complete', onTourComplete)

    // On-demand show (e.g. user tries to enable push from profile while not installed)
    const onForceShow = () => tryShow.current(true)
    window.addEventListener(SHOW_INSTALL_EVENT, onForceShow)

    // Auto-hide when user installs (via our button OR via browser menu)
    const onInstalled = () => setShow(false)
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', capturePrompt)
      window.removeEventListener('tour-complete', onTourComplete)
      window.removeEventListener(SHOW_INSTALL_EVENT, onForceShow)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt.current) return
    deferredPrompt.current.prompt()
    const { outcome } = await deferredPrompt.current.userChoice
    if (outcome === 'accepted') setShow(false)
    deferredPrompt.current = null
  }

  const handleDismiss = async () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setShow(false)

    // If user refused install and notify_push is still at the default (true),
    // flip it to false — they can't receive push until the app is installed anyway.
    // We only overwrite the default to avoid clobbering a later explicit choice.
    if (user?.id && user.notify_push === true) {
      try {
        await supabase.from('profiles').update({ notify_push: false }).eq('id', user.id)
        refreshProfile()
      } catch (err) {
        console.warn('[InstallPrompt] failed to update notify_push:', err)
      }
    }
  }

  if (!show) return null

  return (
    <div className="install-prompt">
      <button className="install-prompt-close" onClick={handleDismiss} aria-label="Chiudi">
        <X size={18} />
      </button>

      <div className="install-prompt-icon"><Smartphone size={24} /></div>

      <div className="install-prompt-body">
        <div className="install-prompt-title">
          {forced ? t('install_required_title') : t('install_title')}
        </div>
        <div className="install-prompt-text">
          {platform === 'ios'
            ? <>{t('install_ios_1')} <Share size={14} style={{ verticalAlign: 'middle', margin: '0 2px' }} /> {t('install_ios_2')}</>
            : (deferredPrompt.current ? t('install_android_text') : t('install_android_manual'))}
        </div>
      </div>

      {platform === 'android' && deferredPrompt.current && (
        <button className="install-prompt-btn" onClick={handleInstall}>
          <Download size={16} />
          {t('install_btn')}
        </button>
      )}

      <button className="install-prompt-dismiss" onClick={handleDismiss}>
        {t('install_dismiss')}
      </button>
    </div>
  )
}
